import * as React from "react";

import {
  clampRailPx,
  railPxFromPointer,
  railSplitBounds,
  RAIL_KEY_STEP_PX,
  SPLIT_MEDIA_QUERY,
  type RailSplitBounds,
} from "@/components/runs/rail-split";
import { GAP_PX } from "@/components/runs/torsion-fit-pairing";
import { cn } from "@/lib/utils";

/**
 * **The handle between the main area and the rail** (flowstack#991 cause 3).
 *
 * `rail-split.ts` carries the argument for taking the divider and not the
 * single border, and owns the clamping law. This file is the DOM: what the
 * handle is, how it moves, and how it says what it is to a reader who is not
 * looking at it.
 *
 * ## It replaces the gap; it does not sit in it
 *
 * The row's `gap-4` is 16px and the divider is exactly {@link GAP_PX} wide,
 * with the row's gap dropped to zero while it renders. That is not tidiness:
 * `torsion-fit-pairing.ts` derives the whole rail ladder from
 * `mainPaneWidthPx`, which subtracts one 16px gap, and at 960px the arithmetic
 * has no slack at all — the main pane lands on its 392px floor exactly. A
 * divider ADDED to the gap would take 16px more and put the curve under the
 * floor the ladder exists to hold, at the one width where it is tight.
 * Occupying the gap keeps every measurement true whether a divider is on
 * screen or not.
 *
 * ## The rail keeps its ladder until the user actually drags
 *
 * `railPx === null` means *nobody has expressed a preference*, and the rail
 * keeps its responsive class ladder — so a window resized across `xl` still
 * moves the rail up a step, which a component that seized the width on mount
 * would have silently stopped doing. The handle still needs a number to report
 * and to start a drag from, and it MEASURES one rather than assuming it
 * ({@link useRailSplit}); the override only exists once a drag or a key press
 * has happened.
 *
 * Double-click — and `Enter` — put it back to `null`, the ladder. That is the
 * escape hatch a resizable split needs and the one most implementations leave
 * out; having it on the pointer alone would have made it a feature a keyboard
 * user does not have, which is the same defect one level down.
 *
 * ## Keyboard, and why `role="separator"`
 *
 * A drag handle reachable only with a pointer is a feature a keyboard user does
 * not have. This is the window-splitter pattern: a focusable
 * `role="separator"` with `aria-orientation`, `aria-valuenow/min/max`,
 * `aria-valuetext` in pixels and `aria-controls` naming the pane it sizes.
 * Arrow keys move it by {@link RAIL_KEY_STEP_PX}; Home/End take it to the ends;
 * `Enter` returns it to the ladder. **Left grows the rail** — the divider moves
 * left, which is the direction the key names.
 *
 * The value it REPORTS is the main pane's width, not the rail's, because the
 * pattern defines `aria-valuenow` against the primary (preceding) pane: the
 * number has to rise as the separator moves right, and reporting the rail made
 * it rise on ArrowLeft. `aria-valuetext` names both widths in words, since the
 * pane a user has in mind is whichever one they are trying to grow.
 *
 * `touch-action: none` on the handle, and pointer capture on the element: both
 * are what make a drag survive the pointer leaving the 16px strip, which it
 * does immediately on any real drag.
 */

/** The handle's own width — exactly the row gap it replaces. */
const DIVIDER_PX = GAP_PX;

export interface RailSplit {
  /** The rail's width override in px, or `null` while the ladder decides. */
  railPx: number | null;
  /** How far the divider may travel, or `null` when it may not — no handle. */
  bounds: RailSplitBounds | null;
  /** The rail's CURRENT width, measured; `null` before the first measurement. */
  measuredRailPx: number | null;
  /** The ROW's width, measured; `null` before the first measurement. The
   * handle reports the MAIN pane, and the main pane is what the row has left
   * over — so this is not a convenience, it is the other half of the sum. */
  rowWidthPx: number | null;
  /** Whether the workspace is side by side at this viewport width. */
  sideBySide: boolean;
  setRailPx: (railPx: number | null) => void;
  rowRef: React.RefObject<HTMLDivElement>;
  railRef: React.RefObject<HTMLDivElement>;
}

/**
 * The split's state, measured from the live layout.
 *
 * Two observations, because they answer different questions: the ROW's width
 * decides the bounds (how much there is to share), and the RAIL's decides what
 * the handle reports and where a drag starts from (how it is shared right now).
 * Both come from `ResizeObserver` rather than from a resize listener, so a
 * change driven by the sidebar collapsing — which moves the row without moving
 * the window — is seen too.
 *
 * `sideBySide` is `matchMedia`, deliberately, rather than a width comparison on
 * the row: the stacked-vs-row decision is made in CSS at the `pair` breakpoint
 * against the VIEWPORT, and re-deriving it from a measured element would be a
 * second rule that can disagree with the first. In jsdom `matchMedia` is
 * stubbed to `matches: false`, so a component test renders no handle unless it
 * says otherwise — which is the right default for a control that only exists in
 * a layout jsdom cannot produce.
 */
export function useRailSplit(): RailSplit {
  const rowRef = React.useRef<HTMLDivElement>(null);
  const railRef = React.useRef<HTMLDivElement>(null);
  const [railPx, setRailPx] = React.useState<number | null>(null);
  const [rowWidth, setRowWidth] = React.useState<number | null>(null);
  const [measuredRailPx, setMeasuredRailPx] = React.useState<number | null>(null);
  const [sideBySide, setSideBySide] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const query = window.matchMedia(SPLIT_MEDIA_QUERY);
    setSideBySide(query.matches);
    const onChange = (event: MediaQueryListEvent): void => setSideBySide(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  React.useEffect(() => {
    const row = rowRef.current;
    const rail = railRef.current;
    if (row === null || rail === null || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      setRowWidth(row.getBoundingClientRect().width);
      setMeasuredRailPx(rail.getBoundingClientRect().width);
    });
    observer.observe(row);
    observer.observe(rail);
    return () => observer.disconnect();
  }, []);

  const bounds = rowWidth === null ? null : railSplitBounds(rowWidth);

  // The override is re-clamped against the CURRENT bounds rather than only at
  // the moment it is set. A window narrowed after a drag would otherwise keep a
  // rail wide enough to put the curve under its floor — the drag's clamp was
  // computed against a row that no longer exists.
  const clamped = railPx === null || bounds === null ? railPx : clampRailPx(railPx, bounds);

  return {
    railPx: clamped,
    bounds,
    measuredRailPx,
    rowWidthPx: rowWidth,
    sideBySide,
    setRailPx,
    rowRef,
    railRef,
  };
}

/**
 * The handle itself.
 *
 * Renders nothing without bounds to move in, or before the rail has been
 * measured — see {@link railSplitBounds}. A handle that cannot move would be a
 * control the user learns is a lie, and one reporting a width nobody has
 * measured yet would be worse: `aria-valuenow` would be a guess.
 */
export function RailSplitter({
  split,
  label,
  controls,
}: {
  split: RailSplit;
  /** What the handle resizes, for its accessible name — the workspace's own
   * words, so a page with two of them could never be ambiguous. */
  label: string;
  /** The `id` of the pane this separator sizes, for `aria-controls`. The ARIA
   * window-splitter pattern uses it to tie the separator to the pane its value
   * describes; without it assistive technology hears a resize control and
   * cannot say WHICH region it resizes. */
  controls?: string;
}): JSX.Element | null {
  const { bounds, railPx, measuredRailPx, rowWidthPx, setRailPx, rowRef } = split;
  const [dragging, setDragging] = React.useState(false);
  const current = railPx ?? measuredRailPx;

  if (bounds === null || current === null || rowWidthPx === null) return null;

  // THE VALUE IS THE MAIN PANE'S WIDTH, not the rail's.
  //
  // In the WAI-ARIA window-splitter pattern `aria-valuenow` is the separator's
  // position with respect to the PRIMARY (preceding) pane, so the value rises
  // as the separator moves right. Reporting the rail — the following pane —
  // inverted that: ArrowLeft grew the number. `aria-valuetext` said so in
  // words, but several screen readers announce the numeric value or its
  // percentage for a range widget and never reach the text, and a user hearing
  // the number go up while pressing Left is being told the opposite of what
  // happened. Nothing is lost by reporting the main pane instead — the rail's
  // width is `rowWidth - DIVIDER_PX - value` — and `aria-valuetext` still names
  // the pane in words, so the announcement is unambiguous either way.
  //
  // The bounds inverT with the value: a WIDER rail is a NARROWER main pane, so
  // the rail's `max` is the main pane's `min`.
  const mainPaneWidth = (rail: number): number => rowWidthPx - DIVIDER_PX - rail;
  const valueNow = mainPaneWidth(current);
  const valueMin = mainPaneWidth(bounds.max);
  const valueMax = mainPaneWidth(bounds.min);

  const move = (next: number): void => setRailPx(clampRailPx(next, bounds));

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    // Primary button only; a right-click on a drag handle should open the
    // context menu, not start a drag the user cannot see they began.
    if (event.button !== 0) return;
    if (rowRef.current === null) return;
    event.preventDefault();
    // Feature-guarded because jsdom does not implement pointer capture at all:
    // unguarded, any test that exercises the real drag path throws on the
    // first `pointerdown` instead of asserting anything. A browser always has
    // it, so the guard costs nothing where it matters.
    if (typeof event.currentTarget.setPointerCapture === "function") {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    setDragging(true);
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (!dragging) return;
    const row = rowRef.current;
    if (row === null) return;
    move(
      railPxFromPointer({
        pointerX: event.clientX,
        rowRight: row.getBoundingClientRect().right,
        dividerWidthPx: DIVIDER_PX,
      })
    );
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (!dragging) return;
    // Same guard, same reason — and `lostpointercapture` is wired below so the
    // drag also ends when the capture is taken away rather than released.
    if (
      typeof event.currentTarget.hasPointerCapture === "function" &&
      event.currentTarget.hasPointerCapture(event.pointerId)
    ) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setDragging(false);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    // LEFT grows the rail: the divider moves the way the key names, and the
    // value reported is the rail's width — see the file docstring.
    switch (event.key) {
      case "ArrowLeft":
        move(current + RAIL_KEY_STEP_PX);
        break;
      case "ArrowRight":
        move(current - RAIL_KEY_STEP_PX);
        break;
      case "Home":
        move(bounds.max);
        break;
      case "End":
        move(bounds.min);
        break;
      // BACK TO THE LADDER, from the keyboard. Double-click was the only way
      // there, which made the escape hatch a pointer-only feature — a keyboard
      // user could move the rail off the responsive ladder and had no way to
      // put it back. `Enter` is the key the window-splitter pattern reserves
      // for the separator's own action (the APG spends it on collapse, which
      // this splitter does not do), and it is the one key a user who has
      // focused a control tries first.
      //
      // Not `Escape`: that is the expanded frame's way out, listened for on
      // `window`, and a separator swallowing it would trap a user inside the
      // frame to reset a rail width.
      case "Enter":
        setRailPx(null);
        break;
      default:
        return;
    }
    // Only for a key this handle acted on. Arrow keys scroll the page and
    // Home/End jump it, so the default has to go — but swallowing Tab or
    // Escape would trap focus and take the frame's own way out with it.
    event.preventDefault();
  };

  return (
    // A `div` with `role="separator"` and a tab stop — the ARIA **focusable
    // separator** (window splitter). Not a `<button>`: a button announces "you
    // may press this", and pressing a splitter does nothing; its value is the
    // `aria-value*` triple, which is what a separator carries and a button does
    // not. jsx-a11y's role tables mark `separator` non-interactive
    // unconditionally, which is true of the DECORATIVE separator and false of
    // this one — so the two rules are turned off HERE, on the exact lines they
    // report, rather than for the file: a file-scoped exemption would silently
    // cover any interactive markup a later edit adds elsewhere in it. (Two
    // comments and not one because the rules report different lines: the
    // handlers are read off the opening tag, the tab stop off its own
    // attribute.)
    /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize ${label}`}
      // The MAIN pane's width, per the window-splitter convention — see the
      // derivation above. `aria-controls` names the pane that value describes,
      // so a reader is told which region moves and not merely that one does.
      aria-controls={controls}
      aria-valuenow={valueNow}
      aria-valuemin={valueMin}
      aria-valuemax={valueMax}
      // The number alone would be a position on an unnamed scale. Both widths,
      // because the pane a user is thinking about is whichever one they are
      // trying to make bigger.
      aria-valuetext={`main pane ${valueNow} pixels, rail ${current} pixels`}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      data-testid="rail-splitter"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onLostPointerCapture={endDrag}
      onKeyDown={onKeyDown}
      // Back to the ladder. A resizable split needs a way back to the width
      // somebody chose on purpose; double-click is where a pointer user looks
      // for it and `Enter` is the keyboard's way to the same place.
      onDoubleClick={() => setRailPx(null)}
      title="Drag to resize · double-click or Enter to reset"
      className={cn(
        // `w-4` IS the row gap it replaces (see the file docstring), `shrink-0`
        // so it cannot be squeezed out of existence by two panes competing, and
        // `touch-action: none` so a touch drag resizes rather than scrolls.
        "group hidden w-4 shrink-0 cursor-col-resize touch-none select-none pair:flex",
        "items-center justify-center rounded-full",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      )}
    >
      {/* The visible line: a hairline at rest, the border's own colour, so the
          handle reads as the seam BETWEEN two panes rather than as a third
          object between them. It is the container contract's neutral ladder and
          nothing else — a visual container never spends a hue on chrome. It
          thickens and takes the foreground ink on hover, focus and drag, which
          is what makes it findable without drawing attention at rest. */}
      <span
        aria-hidden
        className={cn(
          "h-10 w-px rounded-full bg-border-subtle transition-all",
          "group-hover:w-1 group-hover:bg-muted-foreground",
          "group-focus-visible:w-1 group-focus-visible:bg-muted-foreground",
          dragging && "w-1 bg-foreground"
        )}
      />
    </div>
  );
}
